//status service.ts
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import {
    IndicatorMessage,
    UserStatus
} from "../models/conversation.model";
import { SharedService } from "../../../services/shared.service";

/**
 * A custom type guard that checks if the provided data is a single UserStatus object.
 * It does this by checking for the existence of the `userId` property.
 * If it returns true, TypeScript will treat `data` as `UserStatus` inside the `if` block.
 */
function isSingleUserStatus(data: UserStatus | Record<string, UserStatus>): data is UserStatus {
    return (data as UserStatus).userId !== undefined;
}

@Injectable({
    providedIn: "root",
})
export class StatusService {
    private userStatuses = new BehaviorSubject<Map<number, UserStatus>>(new Map());
    public userStatuses$: Observable<Map<number, UserStatus>> = this.userStatuses.asObservable();

    private currentUserStatus = new BehaviorSubject<UserStatus | null>(null);
    public currentUserStatus$: Observable<UserStatus | null> = this.currentUserStatus.asObservable();

    constructor(private sharedService: SharedService) {}

    public handleIncomingMessage(message: any): void {
        if (message && message.messageType && message.messageType === "INDICATOR") {
            const indicatorMessage = message as IndicatorMessage;
            const currentStatuses = this.userStatuses.getValue();
            // --- New Type Guard in Action ---
            if (isSingleUserStatus(indicatorMessage.data)) {
                // --- Handle SINGLE update ---
                const userStatus = indicatorMessage.data;
                currentStatuses.set(userStatus.userId, userStatus);
            } else {
                // --- Handle BULK update ---
                const allNewStatuses = indicatorMessage.data;
                Object.values(allNewStatuses).forEach((userStatus) => {
                    currentStatuses.set(userStatus.userId, userStatus);
                });
            }
            // Emit a new map to notify all subscribers
            this.userStatuses.next(new Map(currentStatuses));
            this.updateCurrentUserStatus();
        }
    }

    getUserStatus(userId: number): UserStatus | null {
        const currentStatuses = this.userStatuses.getValue();
        return currentStatuses.get(userId) || null;
    }

    updateCurrentUserStatus() {
        const currentUserId = this.sharedService.getLoggedInUserId();
        if (!currentUserId) {
            console.error("Current user ID is not available. Cannot update status.");
            return;
        }
        const currentStatuses = this.userStatuses.getValue();
        const currentStatus = currentStatuses.get(currentUserId);
        if (currentStatus) {
            this.currentUserStatus.next(currentStatus);
        } else {
            this.currentUserStatus.next(null);
        }
    }
}
